import React, { useRef, useState } from 'react';
import { PaperClipIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type { CategoriaAnexoAtivo } from '../../../../models';
import { CATEGORIA_ANEXO_LABELS } from './AtivoAnexos';

export interface AnexoPendente {
  id: string;
  arquivo: File;
  categoria: CategoriaAnexoAtivo;
}

interface AtivoAnexosPendentesProps {
  anexos: AnexoPendente[];
  onChange: (anexos: AnexoPendente[]) => void;
}

/**
 * Aba Anexos no cadastro NOVO: o ativo ainda não existe (o arquivo precisa do id pra ir pra
 * pasta dele), então os arquivos só ficam separados aqui e sobem logo depois de
 * "Cadastrar ativo" (ver AtivoFichaForm.salvar).
 */
export default function AtivoAnexosPendentes({ anexos, onChange }: AtivoAnexosPendentesProps) {
  const [categoria, setCategoria] = useState<CategoriaAnexoAtivo>('nota_fiscal');
  const inputRef = useRef<HTMLInputElement>(null);

  const adicionar = (arquivos: FileList | null) => {
    if (!arquivos?.length) return;
    onChange([...anexos, ...Array.from(arquivos).map((arquivo) => ({ id: crypto.randomUUID(), arquivo, categoria }))]);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        Os arquivos escolhidos aqui são enviados assim que o ativo for cadastrado.
      </p>
      <section className="flex flex-wrap items-end gap-3 rounded-md border border-gray-200 p-3">
        <div>
          <label className="block text-sm font-medium text-gray-700">Tipo do arquivo</label>
          <select className="input-primary mt-1" value={categoria} onChange={(e) => setCategoria(e.target.value as CategoriaAnexoAtivo)}>
            {Object.entries(CATEGORIA_ANEXO_LABELS).map(([valor, label]) => (
              <option key={valor} value={valor}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <input ref={inputRef} type="file" multiple className="hidden" onChange={(e) => adicionar(e.target.files)} />
        <button type="button" className="btn-secondary flex items-center gap-1.5 text-sm" onClick={() => inputRef.current?.click()}>
          <PaperClipIcon className="h-4 w-4" />
          Escolher arquivo(s)
        </button>
      </section>

      {anexos.length === 0 ? (
        <p className="text-sm text-gray-400">Nenhum arquivo escolhido.</p>
      ) : (
        <ul className="divide-y divide-gray-100 rounded-md border border-gray-200">
          {anexos.map((anexo) => (
            <li key={anexo.id} className="flex items-center gap-3 px-3 py-2 text-sm">
              <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">{CATEGORIA_ANEXO_LABELS[anexo.categoria]}</span>
              <span className="min-w-0 flex-1 truncate text-gray-700">{anexo.arquivo.name}</span>
              <button
                type="button"
                className="text-gray-400 hover:text-red-600"
                title="Tirar da lista"
                onClick={() => onChange(anexos.filter((a) => a.id !== anexo.id))}
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
